import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";

import { NewsletterService } from "services";
import {
  IAPIState,
  IStoreState,
  IErrorPayload,
  INewsletterSubmitData,
  INewsletterAPIData,
} from "types";

export interface INewletterMeta extends INewsletterAPIData {
  status: IAPIState;
}

export interface INewsletterState {
  [id: string]: {
    meta: INewletterMeta;
  };
}

const initialMeta = {
  status: IAPIState.idle,
};

const initialState: INewsletterState = {};

export const newsletterSubscribe = createAsyncThunk<
  // Return type of the payload creator
  INewsletterAPIData,
  // First argument to the payload creator
  {
    id: string;
    data: INewsletterSubmitData;
  },
  // Types for ThunkAPI
  { rejectValue: IErrorPayload }
>("newsletter/subscribe", async (newsletterData, { rejectWithValue }) => {
  try {
    const { data } = newsletterData;

    return await NewsletterService.subscribe(data);
  } catch (error) {
    return rejectWithValue(error as IErrorPayload);
  }
});

const newsletterSlice = createSlice({
  name: "newsletter",
  initialState,
  reducers: {
    clearNewsletterMeta: (state, { payload }) => {
      state[payload].meta = initialMeta;
    },
  },
  extraReducers: (builder) => {
    // newsletter subscribe pending
    builder.addCase(newsletterSubscribe.pending, (state, { meta }) => {
      state[meta.arg.id] = {
        meta: {
          status: IAPIState.pending,
        },
      };
    });
    // newsletter subscribe failed
    builder.addCase(
      newsletterSubscribe.rejected,
      (state, { meta, payload }) => {
        state[meta.arg.id] = {
          meta: {
            status: IAPIState.failed,
            ...payload,
          },
        };
      }
    );
    // newsletter subscribe succeeded
    builder.addCase(
      newsletterSubscribe.fulfilled,
      (state, { meta, payload }) => {
        state[meta.arg.id] = {
          meta: {
            status: payload.errorCodes?.length
              ? IAPIState.failed
              : IAPIState.succeeded,
            ...payload,
          },
        };
      }
    );
  },
});

// content block id selector
export const newsletterIdSelector = (id: string) =>
  createSelector(
    (state: IStoreState) => state.newsletter,
    (newsletter) => newsletter[id]
  );

export const newsletterMetaSelector = (id: string) =>
  createSelector(
    newsletterIdSelector(id),
    (newsletter) => newsletter?.meta || initialMeta
  );

export const { clearNewsletterMeta } = newsletterSlice.actions;
export const newsletterReducer = newsletterSlice.reducer;
